import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Gauge } from "lucide-react";

import { api, type MonitoringStatus } from "../../api";
import { InfoHint } from "../../components/InfoHint";
import { SearchableSelect } from "../../components/SearchableSelect";
import { Note, SettingsSection } from "./shared";

/**
 * Stream quality: the fleet default, the grid-tile profile, and the advisory
 * ceiling for the original stream.
 *
 * Every rung defined on the server is offered here, 4K included. The
 * Monitoring page hides rungs taller than the analysis buffer for the
 * Detections view only (boxes are drawn on that buffer, so a taller encode
 * would be upscaled) — that cap does not apply to these fleet settings.
 */
export function StreamingSection({ isAdmin }: { isAdmin: boolean }) {
  const qc = useQueryClient();
  const status = useQuery<MonitoringStatus>({
    queryKey: ["monitoring", "status"],
    queryFn: api.monitoring.status,
  });

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ["monitoring", "status"] });

  const setDefault = useMutation({
    mutationFn: (q: string) => api.monitoring.setGlobalQuality(q),
    onSuccess: invalidate,
  });
  const setPassthroughMax = useMutation({
    mutationFn: (q: string) => api.monitoring.setPassthroughMax(q),
    onSuccess: invalidate,
  });
  const setWall = useMutation({
    mutationFn: (p: { height: number; bitrate: number }) =>
      api.monitoring.setWallProfile(p.height, p.bitrate),
    onSuccess: invalidate,
  });

  const quality = status.data?.quality;
  const wall = status.data?.wall_profile;
  const mux = status.data?.mux;

  if (status.isLoading) {
    return (
      <SettingsSection title="Stream quality">
        <div className="bf-card text-sm text-bf-muted">Loading stream settings…</div>
      </SettingsSection>
    );
  }

  if (!quality) {
    return (
      <SettingsSection title="Stream quality">
        <Note tone="warning">
          The live pipeline is not reachable right now, so stream settings
          cannot be shown. {status.data?.deepstream ?? ""}
        </Note>
      </SettingsSection>
    );
  }

  const presets = Object.entries(quality.presets).sort(
    (a, b) => a[1].height - b[1].height,
  );
  const presetOption = ([name, p]: [string, { height: number; bitrate: number }]) => ({
    value: name,
    label: name === "native" ? "Native (match the camera)" : name,
    hint:
      p.height === 0
        ? `Up to the camera's own resolution · ${(p.bitrate / 1e6).toFixed(1)} Mb/s`
        : `${p.height}p · ${(p.bitrate / 1e6).toFixed(1)} Mb/s`,
  });

  const wallValue =
    presets.find(
      ([, p]) => p.height === wall?.height && p.bitrate === wall?.bitrate,
    )?.[0] ?? "";

  return (
    <SettingsSection
      title="Stream quality"
      description="Defaults for live viewing. A change applies the next time a viewer connects — nothing restarts, and nobody watching is interrupted."
    >
      <div className="grid gap-3 lg:grid-cols-3">
        <div className="bf-card space-y-2">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-white">
            <Gauge size={15} /> Default quality
            <InfoHint>
              Used for a single-camera view when that camera has no override of
              its own. Asking for more than the camera sends is capped to the
              camera's own resolution rather than upscaled, so picking 4K on a
              1080p camera costs nothing extra.
            </InfoHint>
          </h3>
          <p className="text-xs text-bf-muted">Single-camera view.</p>
          <SearchableSelect
            ariaLabel="Default stream quality"
            value={quality.default}
            onChange={(v) => setDefault.mutate(v)}
            disabled={!isAdmin || setDefault.isPending}
            searchThreshold={99}
            options={presets.map(presetOption)}
          />
        </div>

        <div className="bf-card space-y-2">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-white">
            <Gauge size={15} /> Grid tiles
            <InfoHint>
              Quality for each tile in the multi-camera grid. Set separately
              from the single-camera default because a whole wall of
              full-resolution tiles saturates most uplinks.
            </InfoHint>
          </h3>
          <p className="text-xs text-bf-muted">Multi-camera grid.</p>
          <SearchableSelect
            ariaLabel="Grid tile quality"
            value={wallValue}
            onChange={(name) => {
              const p = quality.presets[name];
              if (p) setWall.mutate({ height: p.height, bitrate: p.bitrate });
            }}
            disabled={!isAdmin || setWall.isPending}
            searchThreshold={99}
            placeholder={
              wall ? `${wall.height}p · ${(wall.bitrate / 1e6).toFixed(1)} Mb/s` : "—"
            }
            options={presets
              // "native" makes no sense for a 16-tile grid.
              .filter(([, p]) => p.height > 0)
              .map(presetOption)}
          />
        </div>

        <div className="bf-card space-y-2">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-white">
            <Gauge size={15} /> Original stream ceiling
            <InfoHint>
              The original stream is relayed untouched, so this cannot be
              enforced on the GPU. It is the point at which a viewer is warned
              to lower the resolution at the camera, or switch to a re-encoded
              view, before connecting to something their machine cannot decode.
            </InfoHint>
          </h3>
          <p className="text-xs text-bf-muted">Advisory only.</p>
          <SearchableSelect
            ariaLabel="Original stream ceiling"
            value={quality.passthrough_max}
            onChange={(v) => setPassthroughMax.mutate(v)}
            disabled={!isAdmin || setPassthroughMax.isPending}
            searchThreshold={99}
            options={presets.map(([name, p]) => ({
              value: name,
              label: name === "native" ? "No ceiling" : name,
              hint: p.height === 0 ? "Carry whatever the camera sends" : `${p.height}p`,
            }))}
          />
        </div>
      </div>

      {mux && (
        <Note tone="info">
          Analysis runs at {mux.width}×{mux.height}. The Detections view is drawn
          on those buffers, so its quality menu stops there
          {mux.height < 2160 && " (4K is offered for the other views)"}. Changing
          the analysis resolution is a pipeline setting, not a viewing one.
        </Note>
      )}

      {!isAdmin && (
        <Note tone="info">
          These are shown read-only — an administrator can change them.
        </Note>
      )}
    </SettingsSection>
  );
}
